import { useCallback, useEffect, useState } from 'react';

import { Api } from '../../api/api';
import { reportAccessDeniedInChildMode } from '../ChildHome/useChildHome';

import type { AchievementsState } from './AchievementsState';

/**
 * Reads the child's own achievements.
 * Mirrors android/…/Screens/Achievements/AchievementsViewModel.kt.
 *
 * `childId` is null in child mode — the child token identifies them — and set in parent mode, where the
 * ownership-checked endpoint is used instead. One hook rather than two because the state and the screen
 * are identical; only whose achievements they are differs.
 */
export function useAchievements(childId: string | null = null) {
  const [state, setState] = useState<AchievementsState>({
    isLoading: true,
    overview: null,
    error: null,
  });

  const load = useCallback(async (): Promise<void> => {
    setState((current) => ({ ...current, isLoading: true, error: null }));

    const result = childId === null ? await Api.readAchievements() : await Api.readChildAchievements(childId);

    if (result.ok) {
      setState({ isLoading: false, overview: result.value, error: null });

      return;
    }

    // In child mode a 403 is a routing defect, not something that happened to the child.
    const isRoutingDefect = childId === null && result.error.kind === 'accessDenied';

    if (isRoutingDefect) reportAccessDeniedInChildMode('achievements');

    setState({
      isLoading: false,
      overview: null,
      error: isRoutingDefect ? null : result.error.message,
    });
  }, [childId]);

  useEffect(() => {
    void load();
  }, [load]);

  return { state, load };
}
